import React, { useMemo, useState } from 'react';
import { useI18n } from '../../context/I18nContext';
import { Icons } from '../icons/Icons';
import CableTessera from './CableTessera';
import { exportSchematicToPDFColor, exportSchematicNestedToPDF } from '../../utils/export';
import HybridCableVisualization from './HybridCableVisualization';
import { createSortContext, universalSort } from '../../utils/sortingLogic';
import { getDisplayTitle, getCableDisplayName, analyzeAndGroupCables } from '../../utils/cableLogic';
import { useGroupedCables } from '../../hooks/useGroupedCables';
import { getGroupDisplayTitle } from '../../utils/groups';
import { buildNestedCableStructure } from '../../utils/nestedCableStructure';
import SchematicNestedVisualization from './SchematicNestedVisualization';

// Componente visualizzazione schematica
const SchematicCableView = ({ 
    cables, 
    settings, 
    setListSortMode, 
    hoveredCable, 
    setHoveredCable, 
    isMobile,
    // Funzioni per la gestione dei cavi
    editCable,
    editMultipolarCable,
    deleteCable,
    deleteMultipolarCable,
    validateCable,
    editingIndex,
    editingMultipolarIndex,
    showDeleteButtons,
    toggleDeleteButton,
    // Props per la selezione dei cavi
    isSelectionModeEnabled,
    selectedCables,
    // Guaine
    availableSheaths = []
}) => {
    const { t } = useI18n();
    const sortOptions = [
        { value: 'numerazione', label: t('schematic.numbering') },
        { value: 'siglatura', label: t('schematic.signature') },
        { value: 'colore', label: t('schematic.color') },
        { value: 'sezione', label: t('schematic.section') },
        { value: 'coloreSezione', label: t('schematic.colorSection') },
        { value: 'gruppo', label: t('schematic.groupSort') }
    ];

    const [localHoveredCable, setLocalHoveredCable] = useState(null);
    const [dynamicGroupsDivision, setDynamicGroupsDivision] = useState(false);
    const [debugMode, setDebugMode] = useState(false);
    const [useNestedSchematic, setUseNestedSchematic] = useState(false);

    // Filtra i cavi in base alla selezione per il raggruppamento
    const cablesForGrouping = isSelectionModeEnabled && selectedCables.size > 0 
        ? cables.filter((_, index) => selectedCables.has(index))
        : cables;
    
    
    // Usa il hook per il raggruppamento dei cavi
    const { groupCablesByMode } = useGroupedCables(cablesForGrouping, settings.listSortMode, settings);
    const originalGroupedCables = useMemo(() => groupCablesByMode(), [groupCablesByMode]);
    const nestedBaseGroups = useMemo(() => {
        if (dynamicGroupsDivision) {
            return divideGroupsIntoSubgroups(originalGroupedCables);
        }
        return originalGroupedCables;
    }, [originalGroupedCables, dynamicGroupsDivision, settings]);

    const nestedSchematicNodes = useMemo(() => {
        const forcedGroups = [];
        const normalCables = [];

        Object.entries(nestedBaseGroups || {}).forEach(([key, groupData]) => {
            if (groupData?.type === 'forced_group') {
                forcedGroups.push({
                    isForcedGroup: true,
                    key,
                    groupData
                });
            } else if (Array.isArray(groupData)) {
                normalCables.push(...groupData);
            }
        });

        return buildNestedCableStructure({
            settings,
            forcedGroups,
            normalCables
        });
    }, [nestedBaseGroups, settings]);

    const renderLegacyGroup = ([siglatura, groupData]) => {
        if (groupData.type === 'forced_group') {
            const sortedCables = groupData.allCables;
            const subGroups = groupData.subGroups;
            const nomeGruppo = groupData.groupName;

            return (
                <div
                    key={siglatura}
                    id={`cable-group-${siglatura}`}
                    className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
                >
                    <div className="mb-6">
                        <div className="flex items-center justify-between">
                            <div className="flex-1 mr-4">
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-gray-800">
                                        {getGroupDisplayTitleWithSubgroup(groupData, settings)}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        {sortedCables.length === 1
                                            ? `1 ${t('schematic.cable')}`
                                            : `${sortedCables.length} ${t('schematic.cables')}`}
                                        {subGroups && subGroups.length > 1 ? ` ${t('schematic.cablesInSubgroups', { count: subGroups.length })}` : ''}
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {t('schematic.groupLabel')} {nomeGruppo}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-6 bg-gray-50 rounded-lg p-4">
                        <HybridCableVisualization
                            cables={sortedCables}
                            settings={settings}
                            siglatura={siglatura}
                            hoveredCable={localHoveredCable}
                            setHoveredCable={setLocalHoveredCable}
                            isMobile={isMobile}
                            availableSheaths={availableSheaths}
                        />
                    </div>

                    {!isMobile && (
                        <div className="bg-blue-50 rounded-lg p-6 h-full flex flex-col">
                            <div className="space-y-2">
                                {sortedCables.map((cable, idx) => {
                                    const isHovered = localHoveredCable === cable.originalIndex;
                                    const displaySiglatura = getCableDisplayName(cable, settings.lingua, settings);

                                    return (
                                        <div
                                            key={`${cable.originalIndex}-${idx}`}
                                            className="transition-all duration-200"
                                            onMouseEnter={() => setLocalHoveredCable(cable.originalIndex)}
                                            onMouseLeave={() => setLocalHoveredCable(null)}
                                            style={isHovered ? {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 8px rgba(59, 130, 246, 0.3)'
                                            } : {}}
                                        >
                                            <CableTessera
                                                cable={cable}
                                                displaySiglatura={displaySiglatura}
                                                onCableClick={null}
                                                onEditCable={editCable}
                                                onEditMultipolarCable={editMultipolarCable}
                                                onDeleteCable={deleteCable}
                                                onDeleteMultipolarCable={deleteMultipolarCable}
                                                showSiglatura={true}
                                                clickable={false}
                                                settings={settings}
                                                validateCable={validateCable}
                                                isHovered={isHovered}
                                                editingIndex={editingIndex}
                                                editingMultipolarIndex={editingMultipolarIndex}
                                                showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
                                                onToggleDelete={() => toggleDeleteButton(cable.originalIndex)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        const sortedCables = Array.isArray(groupData) ? groupData : [];
        const subGroups = analyzeAndGroupCables(sortedCables, settings);
        const hasMultipleSubGroups = subGroups.length > 1;
        const hasMixedTypes = subGroups.some(g => g.isSingle) && subGroups.some(g => !g.isSingle);

        return (
            <div
                key={siglatura}
                id={`cable-group-${siglatura}`}
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
            >
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800">
                                    {getDisplayTitleWithSubgroup(siglatura, sortedCables, false, null, settings)}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {(() => {
                                        const isSheathGroup = siglatura && siglatura.startsWith('sheath_');

                                        if (sortedCables.length === 1) {
                                            return t('schematic.singleCable');
                                        } else if (isSheathGroup) {
                                            return `${sortedCables.length} ${t('schematic.cables')}`;
                                        } else {
                                            return `${sortedCables.length} ${t('schematic.cableSegments')}${hasMultipleSubGroups ? ` ${t('schematic.cableSegmentsInGroups', { count: subGroups.length })}` : ''}`;
                                        }
                                    })()}
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-2 flex-shrink-0">
                            {hasMixedTypes && (
                                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {t('schematic.hybrid')}
                                </div>
                            )}
                            {sortedCables.length > 1 && !hasMixedTypes && (() => {
                                const isSheathGroup = siglatura && siglatura.startsWith('sheath_');
                                return isSheathGroup ? (
                                    <div className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {t('schematic.sheath')}
                                    </div>
                                ) : (
                                    <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                                        {t('schematic.multiple')}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                <div className="mb-6 bg-gray-50 rounded-lg p-4">
                    <HybridCableVisualization
                        cables={sortedCables}
                        settings={settings}
                        siglatura={siglatura}
                        hoveredCable={localHoveredCable}
                        setHoveredCable={setLocalHoveredCable}
                        isMobile={isMobile}
                        availableSheaths={availableSheaths}
                    />
                </div>

                {!isMobile && (
                    <div className="bg-blue-50 rounded-lg p-6 h-full flex flex-col">
                        <div className="space-y-2">
                            {sortedCables.map((cable, idx) => {
                                const isHovered = localHoveredCable === cable.originalIndex;
                                const displaySiglatura = getCableDisplayName(cable, settings.lingua, settings);

                                return (
                                    <div
                                        key={`${cable.originalIndex}-${idx}`}
                                        className="transition-all duration-200"
                                        onMouseEnter={() => setLocalHoveredCable(cable.originalIndex)}
                                        onMouseLeave={() => setLocalHoveredCable(null)}
                                        style={isHovered ? {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 8px rgba(59, 130, 246, 0.3)'
                                        } : {}}
                                    >
                                        <CableTessera
                                            cable={cable}
                                            displaySiglatura={displaySiglatura}
                                            onCableClick={null}
                                            onEditCable={editCable}
                                            onEditMultipolarCable={editMultipolarCable}
                                            onDeleteCable={deleteCable}
                                            onDeleteMultipolarCable={deleteMultipolarCable}
                                            showSiglatura={true}
                                            clickable={false}
                                            settings={settings}
                                            validateCable={validateCable}
                                            isHovered={isHovered}
                                            editingIndex={editingIndex}
                                            editingMultipolarIndex={editingMultipolarIndex}
                                            showDeleteButton={showDeleteButtons.has(cable.originalIndex)}
                                            onToggleDelete={() => toggleDeleteButton(cable.originalIndex)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Funzione per ottenere il titolo del gruppo con sottogruppo
    const getGroupDisplayTitleWithSubgroup = (groupData, settings) => {
        const baseTitle = getGroupDisplayTitle(groupData.groupName, settings.groupTitles);
        
        if (dynamicGroupsDivision && groupData.subgroupIndex && groupData.totalSubgroups) {
            return `${baseTitle} (${groupData.subgroupIndex}/${groupData.totalSubgroups})`;
        }
        
        return baseTitle;
    };

    // Funzione per ottenere il titolo del gruppo normale con sottogruppo
    const getDisplayTitleWithSubgroup = (siglatura, sortedCables, isGrouppoForzato, nomeGruppo, settings) => {
        // Gestione speciale per gruppi guaina
        if (siglatura && siglatura.startsWith('sheath_')) {
            const sheathId = parseInt(siglatura.replace('sheath_', ''));
            const sheath = availableSheaths.find(s => s.id === sheathId);
            const baseTitle = sheath ? (sheath.fullLabel || `${t('schematic.sheathLabel')} ${sheath.label}`) : t('schematic.sheathNumber', { id: sheathId });
            
            if (dynamicGroupsDivision && sortedCables.length > 0 && sortedCables[0].subgroupIndex && sortedCables[0].totalSubgroups) {
                return `${baseTitle} (${sortedCables[0].subgroupIndex}/${sortedCables[0].totalSubgroups})`;
            }
            
            return baseTitle;
        }
        
        const baseTitle = getDisplayTitle(siglatura, sortedCables, isGrouppoForzato, nomeGruppo, settings);
        
        if (dynamicGroupsDivision && sortedCables.length > 0 && sortedCables[0].subgroupIndex && sortedCables[0].totalSubgroups) {
            return `${baseTitle} (${sortedCables[0].subgroupIndex}/${sortedCables[0].totalSubgroups})`;
        }
        
        return baseTitle;
    };

    // Funzione per calcolare l'altezza stimata di un gruppo di cavi
    function calculateGroupHeight(cables, settings, usableWidth = 800, groupIdentifier = null) {
        let groupHeight = 12; // Header colorato
        
        // Replica la logica dell'export: cerca gli SVG nel DOM
        const schematicContainer = document.getElementById('schematic-container');
        
        if (!schematicContainer) {
            // Fallback alle stime se il DOM non è disponibile
            const scale = (usableWidth - 6) / 800;
            let totalSvgHeight = 0;
            
            // Raggruppa i cavi per guaina per il calcolo dell'altezza
            const cablesWithSheath = cables.filter(c => c.guaina && !(c.poli && Array.isArray(c.poli)));
            const cablesWithoutSheath = cables.filter(c => !c.guaina && !(c.poli && Array.isArray(c.poli)));
            const multipolarCables = cables.filter(c => c.poli && Array.isArray(c.poli));
            
            const sheathGroups = {};
            cablesWithSheath.forEach(cable => {
                if (!sheathGroups[cable.guaina]) {
                    sheathGroups[cable.guaina] = [];
                }
                sheathGroups[cable.guaina].push(cable);
            });
            
            // Calcola altezza per multipolari
            multipolarCables.forEach(cable => {
                const svgHeight = Math.max(200, 80 + (cable.poli.length * 60));
                totalSvgHeight += (svgHeight * scale) + 4;
            });
            
            // Calcola altezza per gruppi guaina (stessa logica dei multipolari)
            Object.values(sheathGroups).forEach(groupCables => {
                const svgHeight = Math.max(200, 80 + (groupCables.length * 60));
                totalSvgHeight += (svgHeight * scale) + 4;
            });
            
            // Calcola altezza per cavi standard senza guaina
            cablesWithoutSheath.forEach(cable => {
                const svgHeight = 250; // Altezza fissa per desktop
                totalSvgHeight += (svgHeight * scale) + 4;
            });
            
            groupHeight += totalSvgHeight;
            return groupHeight;
        }
        
        // IDENTIFICA IL GRUPPO CORRETTO NEL DOM
        let targetGroup = null;
        let methodUsed = '';
        
        if (groupIdentifier) {
            // Usa l'identificatore del gruppo se fornito
            targetGroup = document.getElementById(`cable-group-${groupIdentifier}`);
            if (targetGroup) {
                methodUsed = `DOM con ID: cable-group-${groupIdentifier}`;
            }
        }
        
        if (!targetGroup) {
            // Fallback: cerca il gruppo che contiene i cavi specifici
            const cableGroups = schematicContainer.querySelectorAll('[id^="cable-group-"]');
            targetGroup = Array.from(cableGroups).find(group => {
                const cableNumbers = Array.from(group.querySelectorAll('span')).map(span => 
                    parseInt(span.textContent?.replace('#', '')) - 1
                );
                const expectedNumbers = cables.map(c => c.originalIndex);
                return expectedNumbers.every(num => cableNumbers.includes(num));
            });
            if (targetGroup) {
                methodUsed = `DOM con ricerca cavi: ${cables.map(c => c.originalIndex).join(',')}`;
            }
        }
        
        if (!targetGroup) {
            // Fallback finale: usa stime fisse
            methodUsed = `STIME FISSE (gruppo non trovato)`;
            const scale = (usableWidth - 6) / 800;
            let totalSvgHeight = 0;
            
            cables.forEach(cable => {
                if (cable.poli && Array.isArray(cable.poli)) {
                    const svgHeight = Math.max(200, 80 + (cable.poli.length * 60));
                    totalSvgHeight += (svgHeight * scale) + 4;
                } else {
                    const svgHeight = 250;
                    totalSvgHeight += (svgHeight * scale) + 4;
                }
            });
            
            groupHeight += totalSvgHeight;
            console.log(`[DEBUG calculateGroupHeight] ${methodUsed} - Altezza: ${groupHeight.toFixed(2)}mm`);
            return groupHeight;
        }
        
        // IDENTICO ALL'EXPORT: Calcola altezza del gruppo specifico
        // Ma solo per i cavi specifici passati alla funzione
        const expectedCableNumbers = cables.map(c => c.originalIndex);
        const svgs = targetGroup.querySelectorAll('.bg-gray-50.rounded-lg.p-4 svg, .bg-gray-50.rounded-lg.p-2 svg');
        let totalSvgHeight = 0;
        let processedCables = [];
        
        for (let j = 0; j < svgs.length; j++) {
            // Trova il cavo associato a questo SVG usando data-cable-index
            const svgContainer = svgs[j].closest('.bg-gray-50.rounded-lg');
            const cableIndexAttr = svgContainer?.getAttribute('data-cable-index');
            const cableIndicesAttr = svgContainer?.getAttribute('data-cable-indices');
            
            let cableNumbers = [];
            if (cableIndexAttr) {
                // Singolo cavo
                cableNumbers = [parseInt(cableIndexAttr)];
            } else if (cableIndicesAttr) {
                // Multipli cavi
                cableNumbers = cableIndicesAttr.split(',').map(n => parseInt(n));
            }
            
            // Calcola solo se almeno uno dei cavi è tra quelli richiesti
            const hasRequiredCable = cableNumbers.some(cableNum => expectedCableNumbers.includes(cableNum));
            if (hasRequiredCable) {
                const viewBox = svgs[j].getAttribute('viewBox').split(' ');
                const svgWidth = parseFloat(viewBox[2]);
                const svgHeight = parseFloat(viewBox[3]);
                const scale = (usableWidth - 6) / svgWidth;
                const scaledHeight = (svgHeight * scale) + 4;
                totalSvgHeight += scaledHeight;
                processedCables.push(...cableNumbers);
            }
        }
        groupHeight += totalSvgHeight;
        
        // IDENTICO ALL'EXPORT: Calcola dettagli del gruppo specifico
        // Ma solo per i cavi specifici passati alla funzione
        const detailsSection = targetGroup.querySelector('.bg-blue-50.rounded-lg.p-6');
        const cableDetails = detailsSection ? detailsSection.querySelectorAll('.space-y-2 > div') : [];
        let detailsHeight = 0;
        let processedDetailsCables = [];
        
        if (cableDetails.length > 0) {
            detailsHeight = 12; // Header tabella più spazio
            
            cableDetails.forEach((cableDetailDiv) => {
                const tessera = cableDetailDiv.querySelector('div[class*="border-2"][class*="rounded-lg"]');
                if (tessera) {
                    const cableIndex = parseInt(tessera.querySelector('span')?.textContent?.replace('#', '') - 1);
                    
                    // Calcola solo se questo cavo è tra quelli richiesti
                    if (expectedCableNumbers.includes(cableIndex)) {
                        const cableData = cables.find(c => c.originalIndex === cableIndex);
                        let cableDetailHeight = 0;
                        
                        if (cableData && cableData.poli && Array.isArray(cableData.poli)) {
                            // Cavo multipolare: header + tabella poli
                            cableDetailHeight = 8 + 6 + (cableData.poli.length * 10) + 2; // Header + tabella + poli + separatore
                        } else {
                            // Cavo standard: solo header
                            cableDetailHeight = 8;
                        }
                        
                        detailsHeight += cableDetailHeight;
                        processedDetailsCables.push(cableIndex);
                    }
                }
            });
        }
        
        groupHeight += detailsHeight;
        
        return groupHeight;
    }

    // Funzione per dividere i gruppi in sottogruppi basati sullo spazio disponibile
    function divideGroupsIntoSubgroups(groupedCables) {
        if (!dynamicGroupsDivision) return groupedCables;

        const dividedGroups = {};
        
        const pageHeight = 297; // A4 height in mm
        const margin = 15;
        const maxGroupHeightFirstPage = pageHeight - (margin * 2) - 40;  // 227mm - Prima pagina con header grande
        const maxGroupHeightOtherPages = pageHeight - (margin * 2); // 267mm - Pagine successive con header ridotto
        
        // Usa il valore conservativo (prima pagina) per garantire che i gruppi entrino sempre
        
        Object.entries(groupedCables).forEach(([siglatura, groupData]) => {
            if (groupData.type === 'forced_group') {
                // Per gruppi forzati, dividi in sottogruppi dinamici RISPETTANDO i raggruppamenti logici
                const allCables = groupData.allCables;
                
                // ✅ PRIMA: Analizza la struttura logica dei cavi
                const logicalSubGroups = analyzeAndGroupCables(allCables, settings);
                
                const pageWidth = 210;
                const margin = 15;
                const usableWidth = pageWidth - (2 * margin);
                const scale = (usableWidth - 6) / 800;
                
                // Calcola altezza totale del gruppo - METODO ORIGINALE
                let totalGroupHeight = 12; // Header
                logicalSubGroups.forEach(logicalSubGroup => {
                    if (logicalSubGroup.isSingle) {
                        if (logicalSubGroup.cables[0].poli && Array.isArray(logicalSubGroup.cables[0].poli)) {
                            // Multipolare singolo
                            const cable = logicalSubGroup.cables[0];
                            const svgHeight = Math.max(200, 80 + (cable.poli.length * 60));
                            totalGroupHeight += (svgHeight * scale) + 4;
                            totalGroupHeight += 8 + 6 + (cable.poli.length * 10) + 2;
                        } else {
                            // Standard singolo
                            const svgHeight = 250;
                            totalGroupHeight += (svgHeight * scale) + 4;
                            totalGroupHeight += 8;
                        }
                    } else {
                        // Cavi multipli (condividono SVG)
                        const svgHeight = 250;
                        totalGroupHeight += (svgHeight * scale) + 4;
                        totalGroupHeight += logicalSubGroup.cables.length * 8;
                    }
                });

                // Calcola altezza totale del gruppo
                const totalGroupHeightUnified = calculateGroupHeight(allCables, settings, usableWidth, siglatura);
                
                
                // Se il gruppo entra in una pagina, non dividerlo
                if (totalGroupHeight <= maxGroupHeightFirstPage) {
                    dividedGroups[siglatura] = groupData;
                    return;
                }
                
                // ✅ Il gruppo è troppo alto, dividilo per SOTTOGRUPPI LOGICI
                const finalSubGroups = [];
                let currentSubGroup = [];
                let currentSubGroupHeight = 12; // Header
                let currentSubGroupIndex = 1;
                
                for (let i = 0; i < logicalSubGroups.length; i++) {
                    const logicalSubGroup = logicalSubGroups[i];
                    
                    // Calcola altezza di questo sottogruppo logico - METODO ORIGINALE
                    let subGroupHeight = 0;
                    if (logicalSubGroup.isSingle) {
                        const cable = logicalSubGroup.cables[0];
                        if (cable.poli && Array.isArray(cable.poli)) {
                            // Multipolare
                            const svgHeight = Math.max(200, 80 + (cable.poli.length * 60));
                            subGroupHeight = (svgHeight * scale) + 4;
                            subGroupHeight += 8 + 6 + (cable.poli.length * 10) + 2;
                        } else {
                            // Standard
                            const svgHeight = 250;
                            subGroupHeight = (svgHeight * scale) + 4 + 8;
                        }
                    } else {
                        // Multipli
                        const svgHeight = 250;
                        subGroupHeight = (svgHeight * scale) + 4 + (logicalSubGroup.cables.length * 8);
                    }

                    // Calcola altezza di questo sottogruppo logico
                    const subGroupHeightUnified = calculateGroupHeight(logicalSubGroup.cables, settings, usableWidth, `${siglatura}_${i}`);
                    
                    // Se aggiungere questo sottogruppo logico supera il limite
                    if (currentSubGroupHeight + subGroupHeight > maxGroupHeightFirstPage && currentSubGroup.length > 0) {
                        finalSubGroups.push({
                            cables: currentSubGroup.flat(),
                            subgroupIndex: currentSubGroupIndex,
                            totalSubgroups: 0
                        });
                        
                        currentSubGroup = [];
                        currentSubGroupHeight = 12;
                        currentSubGroupIndex++;
                    }
                    
                    // Aggiungi l'intero sottogruppo logico
                    currentSubGroup.push(logicalSubGroup.cables);
                    currentSubGroupHeight += subGroupHeight;
                }
                
                // Aggiungi l'ultimo sottogruppo
                if (currentSubGroup.length > 0) {
                    finalSubGroups.push({
                        cables: currentSubGroup.flat(),
                        subgroupIndex: currentSubGroupIndex,
                        totalSubgroups: 0
                    });
                }
                
                // Imposta il numero totale di sottogruppi
                finalSubGroups.forEach(subGroup => {
                    subGroup.totalSubgroups = finalSubGroups.length;
                });
                
                // Crea i gruppi divisi
                finalSubGroups.forEach(subGroup => {
                    const subgroupKey = `${siglatura}_${subGroup.subgroupIndex}`;
                    dividedGroups[subgroupKey] = {
                        ...groupData,
                        allCables: subGroup.cables,
                        groupName: groupData.groupName,
                        originalGroupName: groupData.groupName,
                        subgroupIndex: subGroup.subgroupIndex,
                        totalSubgroups: subGroup.totalSubgroups
                    };
                });
            } else {
                // Per gruppi normali, dividi in sottogruppi dinamici RISPETTANDO i raggruppamenti logici
                const groupCables = Array.isArray(groupData) ? groupData : [];
                
                // ✅ PRIMA: Analizza la struttura logica dei cavi
                const logicalSubGroups = analyzeAndGroupCables(groupCables, settings);
                
                const pageWidth = 210; // A4 width in mm
                const margin = 15;
                const usableWidth = pageWidth - (2 * margin);
                const scale = (usableWidth - 6) / 800;
                
                // Calcola altezza totale del gruppo - METODO ORIGINALE
                let totalGroupHeight = 12; // Header
                logicalSubGroups.forEach(logicalSubGroup => {
                    if (logicalSubGroup.isSingle) {
                        // Cavo singolo
                        const svgHeight = 250;
                        totalGroupHeight += (svgHeight * scale) + 4;
                        totalGroupHeight += 8; // Dettagli
                    } else {
                        // Cavi multipli (condividono SVG)
                        const svgHeight = 250;
                        totalGroupHeight += (svgHeight * scale) + 4;
                        totalGroupHeight += logicalSubGroup.cables.length * 8; // Dettagli per ogni cavo
                    }
                });

                // Calcola altezza totale del gruppo
                const totalGroupHeightUnified = calculateGroupHeight(groupCables, settings, usableWidth, siglatura);
                
                // Se il gruppo entra in una pagina, non dividerlo
                if (totalGroupHeight <= maxGroupHeightFirstPage) {
                    dividedGroups[siglatura] = groupData;
                    return;
                }
                
                // ✅ Il gruppo è troppo alto, dividilo per SOTTOGRUPPI LOGICI (non singoli cavi)
                const finalSubGroups = [];
                let currentSubGroup = [];
                let currentSubGroupHeight = 12; // Header
                let currentSubGroupIndex = 1;
                
                for (let i = 0; i < logicalSubGroups.length; i++) {
                    const logicalSubGroup = logicalSubGroups[i];
                    
                    // Calcola altezza di questo sottogruppo logico completo - METODO ORIGINALE
                    let subGroupHeight = 0;
                    if (logicalSubGroup.isSingle) {
                        const svgHeight = 250;
                        subGroupHeight = (svgHeight * scale) + 4 + 8; // SVG + dettagli
                    } else {
                        const svgHeight = 250; // Condiviso tra tutti i cavi del gruppo multiplo
                        subGroupHeight = (svgHeight * scale) + 4 + (logicalSubGroup.cables.length * 8); // SVG + dettagli per ogni cavo
                    }

                    // Calcola altezza di questo sottogruppo logico
                    const subGroupHeightUnified = calculateGroupHeight(logicalSubGroup.cables, settings, usableWidth, `${siglatura}_${i}`);
                    
                    // Se aggiungere questo sottogruppo logico supera il limite
                    if (currentSubGroupHeight + subGroupHeight > maxGroupHeightFirstPage && currentSubGroup.length > 0) {
                        // Salva il sottogruppo corrente
                        finalSubGroups.push({
                            cables: currentSubGroup.flat(), // Appiattisci tutti i cavi dei sottogruppi logici
                            subgroupIndex: currentSubGroupIndex,
                            totalSubgroups: 0 // Sarà calcolato dopo
                        });
                        
                        // Inizia un nuovo sottogruppo
                        currentSubGroup = [];
                        currentSubGroupHeight = 12; // Header
                        currentSubGroupIndex++;
                    }
                    
                    // Aggiungi l'intero sottogruppo logico al sottogruppo corrente
                    currentSubGroup.push(logicalSubGroup.cables);
                    currentSubGroupHeight += subGroupHeight;
                }
                
                // Aggiungi l'ultimo sottogruppo se non è vuoto
                if (currentSubGroup.length > 0) {
                    finalSubGroups.push({
                        cables: currentSubGroup.flat(),
                        subgroupIndex: currentSubGroupIndex,
                        totalSubgroups: 0 // Sarà calcolato dopo
                    });
                }
                
                // Aggiungi il numero totale di sottogruppi
                finalSubGroups.forEach(subGroup => {
                    subGroup.totalSubgroups = finalSubGroups.length;
                });
                
                // Crea i gruppi divisi
                finalSubGroups.forEach(subGroup => {
                    const subgroupKey = `${siglatura}_${subGroup.subgroupIndex}`;
                    dividedGroups[subgroupKey] = subGroup.cables.map(cable => ({
                        ...cable,
                        originalSiglatura: siglatura,
                        subgroupIndex: subGroup.subgroupIndex,
                        totalSubgroups: subGroup.totalSubgroups
                    }));
                });
            }
        });
        
        return dividedGroups;
    }

    const finalGroupsToRender = useMemo(() => {
        const sortedOriginalGroups = Object.entries(originalGroupedCables || {})
            .sort((a, b) => {
                const getFirstIndex = (groupData) => {
                    if (Array.isArray(groupData)) {
                        return Math.min(...groupData.map(c => c.originalIndex));
                    } else if (groupData.type === 'forced_group') {
                        return Math.min(...groupData.allCables.map(c => c.originalIndex));
                    } else {
                        return 0;
                    }
                };

                const firstIndexA = getFirstIndex(a[1]);
                const firstIndexB = getFirstIndex(b[1]);

                if (settings.listSortMode === 'numerazione') {
                    return firstIndexA - firstIndexB;
                }

                const context = createSortContext(settings.lingua);

                const getCablesArray = (groupData) => {
                    if (Array.isArray(groupData)) {
                        return groupData;
                    } else if (groupData.type === 'forced_group') {
                        return groupData.allCables;
                    } else {
                        return [];
                    }
                };

                const groupA = { cables: getCablesArray(a[1]) };
                const groupB = { cables: getCablesArray(b[1]) };

                const [sortedA, sortedB] = universalSort([groupA, groupB], settings.listSortMode, context);
                return sortedA === groupA ? -1 : 1;
            });

        const finalGroups = [];

        sortedOriginalGroups.forEach(([siglatura, groupData]) => {
            if (!dynamicGroupsDivision) {
                finalGroups.push([siglatura, groupData]);
                return;
            }

            const singleGroupObject = { [siglatura]: groupData };
            const dividedGroup = divideGroupsIntoSubgroups(singleGroupObject);

            Object.entries(dividedGroup)
                .sort((a, b) => {
                    if (a[0].includes('_') && b[0].includes('_')) {
                        const [groupA, subA] = a[0].split('_');
                        const [groupB, subB] = b[0].split('_');

                        if (groupA === groupB) {
                            return parseInt(subA, 10) - parseInt(subB, 10);
                        }
                    }
                    return 0;
                })
                .forEach(entry => finalGroups.push(entry));
        });

        return finalGroups;
    }, [originalGroupedCables, dynamicGroupsDivision, settings, divideGroupsIntoSubgroups]);

    return (
        <>
            <div className="mb-4 flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <label className="font-medium">{t('schematic.sortBy')}</label>
                    <select
                        value={settings.listSortMode || 'numerazione'}
                        onChange={e => setListSortMode(e.target.value)}
                        className="border rounded px-2 py-1"
                    >
                        {sortOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex gap-4 items-center">
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="dynamicGroupsDivision"
                            checked={dynamicGroupsDivision}
                            onChange={(e) => setDynamicGroupsDivision(e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="dynamicGroupsDivision" className="text-sm font-medium text-gray-700">
                            Divisione Dinamica Gruppi
                        </label>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="nestedSchematicView"
                            checked={useNestedSchematic}
                            onChange={(e) => setUseNestedSchematic(e.target.checked)}
                            className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <label htmlFor="nestedSchematicView" className="text-sm font-medium text-gray-700">
                            {t('schematic.nestedViewToggle')}
                        </label>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="debugMode"
                            checked={debugMode}
                            onChange={(e) => setDebugMode(e.target.checked)}
                            className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
                        />
                        <label htmlFor="debugMode" className="text-sm font-medium text-gray-700">
                            Debug Mode
                        </label>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                if (cablesForGrouping.length === 0) {
                                    alert(t('schematic.noCablesSelected'));
                                    return;
                                }
                                const exportFn = useNestedSchematic ? exportSchematicNestedToPDF : exportSchematicToPDFColor;
                                exportFn(cablesForGrouping, settings, () => {}, () => {}, setDynamicGroupsDivision, debugMode);
                            }}
                            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors flex items-center gap-2"
                        >
                            <Icons.Download className="w-4 h-4" />
                            {t('schematic.pdfColors')}
                        </button>
                    </div>
                </div>
            </div>
            <div id="schematic-container" className="space-y-8">
                {useNestedSchematic ? (
                    <SchematicNestedVisualization
                        nodes={nestedSchematicNodes}
                        settings={settings}
                        availableSheaths={availableSheaths}
                        isMobile={isMobile}
                        localHoveredCable={localHoveredCable}
                        setLocalHoveredCable={setLocalHoveredCable}
                        editCable={editCable}
                        editMultipolarCable={editMultipolarCable}
                        deleteCable={deleteCable}
                        deleteMultipolarCable={deleteMultipolarCable}
                        validateCable={validateCable}
                        editingIndex={editingIndex}
                        editingMultipolarIndex={editingMultipolarIndex}
                        showDeleteButtons={showDeleteButtons}
                        toggleDeleteButton={toggleDeleteButton}
                        isSelectionModeEnabled={isSelectionModeEnabled}
                        selectedCables={selectedCables}
                    />
                ) : (
                    <div className="space-y-8">
                        {finalGroupsToRender.map(renderLegacyGroup)}
                    </div>
                )}
            </div>
        </>
    );
};

export default SchematicCableView;